import uniqBy from 'lodash/uniqBy';

import {ProjectDetails} from 'common/report/stores/project-details';
import {Persistence} from 'common/report/persistence';
import {isNewUserPage} from 'common/report/util/users/utils';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {objToQueryString} from 'common/util';
import {apiClient} from 'common/util/api-client';
import {findKeyByValue} from 'common/util/typescript';

import {
  RecentSearchItem,
  SearchDisplayContext,
  SearchDisplayContextIcons,
  SearchQueryFilters,
  SearchQuerySort,
  SearchResult,
  SearchResultType,
  TableSort,
} from './types';

const EXPLORE_REPORT_TYPES = [`cohort_users`, `users`];

export function getDisplayContext(item: SearchResult): SearchDisplayContext {
  if (EXPLORE_REPORT_TYPES.includes(item.report)) {
    return SearchDisplayContext.Explore;
  }

  switch (item.type) {
    // Temporarily setting cohorts to Explore on FE
    case SearchResultType.Cohort:
      return SearchDisplayContext.Explore;
    case SearchResultType.Dashboard:
      return SearchDisplayContext.Dashboard;
    default:
      return item.report as SearchDisplayContext;
  }
}

export function getSearchIconFromDisplayContext(displayContext): SearchDisplayContextIcons {
  return SearchDisplayContextIcons[findKeyByValue(SearchDisplayContext, displayContext)];
}

export function getSearchContextsWithMaybeExplore(project: ProjectDetails) {
  return isNewUserPage(project)
    ? Object.values(SearchDisplayContext).filter((context) => context !== SearchDisplayContext.Explore)
    : Object.values(SearchDisplayContext);
}

// SEARCH PERSISTENCE

const contentDiscoveryPersistenceKey = `search`;
let persistence;

export function getRecentItems(): RecentSearchItem[] {
  const projectId = getProjectId();
  // projectId is present on url 99% of the time but for some url e.g settings, it's not.
  // we have to wait for DOMContentLoaded for those cases, so create persistence if it's not already created
  // this check is done here because all persistence related queries stems in this function
  if (projectId) {
    persistence = Persistence.createCachedNamespace(projectId, contentDiscoveryPersistenceKey);
  }
  const items = persistence?.getObject(`recent-items`) || [];
  return items;
}

export function addRecentItem(item: RecentSearchItem) {
  const items = getRecentItems();
  const recentItems = uniqBy([item, ...items], (recentItem) => recentItem.key).slice(0, 50);
  persistence?.setObject(`recent-items`, recentItems);
}

export function removeRecentItem(item: RecentSearchItem) {
  const storedItems = getRecentItems();
  const recentItems = storedItems.filter((storedItem) => storedItem.key !== item.key);
  persistence?.setObject(`recent-items`, recentItems);
}

// SEARCH API

export const DEFAULT_SEARCH_QUERY_STRING = ``;
export const PROMPT_ALLOW_STORAGE_STRING = `Personalize your Mixpanel experience in your user settings`;

function buildSearchQuery(
  queryFilters: SearchQueryFilters,
  querySort: SearchQuerySort | TableSort,
  searchedText: string,
  limit,
) {
  const queryParams = {
    query_filters: JSON.stringify({...queryFilters}),
    ...(querySort ? {query_sort: JSON.stringify({...querySort})} : {}),
    query_string: searchedText,
    limit,
  };

  return objToQueryString(queryParams);
}

export function getUrl() {
  const workspaceId = parseInt(getWorkspaceId(), 10) || null; // null instead of NaN
  if (workspaceId) {
    return `/api/app/workspaces/${workspaceId}/search`;
  }
  return `/api/app/projects/${getProjectId()}/search`;
}

async function loadResults(promise: Promise<any>, {replaceWithEmpty = true} = {}) {
  let results = await promise;
  // Only return if no further requests have been triggered
  if (results || replaceWithEmpty) {
    results = results.filter(
      (item) => ![SearchResultType.Help, SearchResultType.Message, SearchResultType.Campaign].includes(item.type),
    );
  }
  return results;
}

// When using this function, be sure to check for race conditions by accepting the results only if the searched text
// matches the requested search text, or if your request index is the same
export async function executeSearch({
  queryFilters,
  querySort,
  searchedText = ``,
  limit = 100,
  fromFetchCache = true,
}: {
  queryFilters: SearchQueryFilters;
  querySort?: SearchQuerySort | TableSort;
  searchedText?: string;
  limit?: number;
  fromFetchCache?: boolean;
}): Promise<SearchResult[]> {
  const url = `${getUrl()}?${buildSearchQuery(queryFilters, querySort, searchedText, limit)}`;

  if (!fromFetchCache) {
    return loadResults(
      apiClient.get<SearchResult[]>(url, null, {updateFetchCache: true}),
      {},
    );
  }

  // Boosts performance by using cached results when available while
  // always triggering a network call in parallel
  const {cache, network} = apiClient.hydratedGet<SearchResult[]>(url);
  const results = await loadResults(cache, {replaceWithEmpty: false});
  if (results?.length) {
    return results;
  }
  return loadResults(network, {});
}
